import { MatchUpStatusEnum, MatchUpTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { type TFunction } from 'i18next';
import { competitionEngine } from 'tods-competition-factory';

import { isCourtDisabled } from 'components/forms/facility/courts/utils';
import { type CourtType, type MatchUpInterface } from 'typedefs/store/scheduleTypes';
import { factorySafeTimeToDayjs, todsScheduleToDayjs } from 'utils/dateTimeUtils';
import { firstCapital } from 'utils/scheduleUtils';
import { getStatus } from 'utils/statusFormatUtils';

export interface LoadingMatchUpsInterface extends MatchUpInterface {
  dropRowId: string;
}

export interface DateFilterOption {
  text: string;
  value: string;
}

export const scheduleCompletedTableColumns = [
  'index',
  'startTime',
  'tournamentName',
  'event',
  'flights',
  'draw',
  'round',
  'side1',
  'side2',
  'score',
  'actions',
];

export const getTodayDate = (localTimeZone: string) => factorySafeTimeToDayjs(new Date(), localTimeZone).startOf('day');

export const matchUpsFullCategorySort = (a, b, matchUpStageSort, genderSort) => {
  const dateTimeAValue = todsScheduleToDayjs(a?.schedule)?.valueOf() || 0;
  const dateTimeBValue = todsScheduleToDayjs(b?.schedule)?.valueOf() || 0;
  return a?.sortOrder && b?.sortOrder
    ? a?.sortOrder - b?.sortOrder
    : dateTimeAValue - dateTimeBValue ||
        a?.schedule?.venueId?.localeCompare(b?.schedule?.venueId) ||
        b?.matchUpType?.localeCompare(a?.matchUpType) ||
        genderSort?.indexOf(a?.gender) - genderSort?.indexOf(b?.gender) ||
        (a?.category?.ageCategoryCode === 'OPEN'
          ? 0
          : b?.category?.ageCategoryCode === 'OPEN'
            ? 1
            : a?.category?.ageCategoryCode?.localeCompare(b?.category?.ageCategoryCode)) ||
        a?.eventName?.localeCompare(b?.eventName) ||
        matchUpStageSort?.indexOf(a?.stage) - matchUpStageSort?.indexOf(b?.stage);
};

export const matchUpsSortOrderSort = (matchUp: any, matchUpsOrder: any[]) => {
  const sortedMatchUp = matchUpsOrder.find((sortedMatchUp) => sortedMatchUp.matchUpId === matchUp.matchUpId);
  if (sortedMatchUp) {
    return {
      ...matchUp,
      sortOrder: sortedMatchUp.sortOrder,
    };
  }
  return matchUp;
};
export const completedMatchUpsSort = (orderA, orderB) => {
  if (!orderA?.schedule?.endTime && orderB?.schedule?.endTime) {
    return 0;
  }
  if (!orderB?.schedule?.endTime && orderA?.schedule?.endTime) {
    return 1;
  }
  if (!orderA?.schedule?.endTime && !orderB?.schedule?.endTime) {
    return -1;
  }
  return dayjs(orderB?.schedule?.endTime).diff(orderA?.schedule?.endTime);
};

export const getStartAndAvailableDates = (
  tournamentStartDate?: string,
  tournamentEndDate?: string,
  localTimeZone?: string,
) => {
  const { startDate, endDate } =
    !tournamentStartDate && !tournamentEndDate
      ? competitionEngine.getCompetitionDateRange()
      : { startDate: tournamentStartDate, endDate: tournamentEndDate };

  const availableDates: DateFilterOption[] = [];
  const startDateMoment = factorySafeTimeToDayjs(startDate, localTimeZone);
  const endDateMoment = factorySafeTimeToDayjs(endDate, localTimeZone);
  const iteratorDate = factorySafeTimeToDayjs(startDate, localTimeZone);
  for (let m = iteratorDate; m?.diff(endDateMoment, 'days') <= 0; m = m?.add(1, 'day')) {
    availableDates.push({
      text: m?.format('dddd, MMMM Do YYYY'),
      value: m?.format('YYYY-MM-DD'),
    });
  }
  return {
    startDateMoment,
    endDateMoment,
    availableDates,
  };
};

export const calculateCurrentFilterDate = (
  tournamentStartDate?: string,
  tournamentEndDate?: string,
  localTimeZone?: string,
) => {
  const { startDateMoment, endDateMoment, availableDates } = getStartAndAvailableDates(
    tournamentStartDate,
    tournamentEndDate,
    localTimeZone,
  );
  const todayDate = getTodayDate(localTimeZone);
  const tournamentStarted = factorySafeTimeToDayjs(availableDates[0]?.value, localTimeZone)?.isSameOrBefore(todayDate);
  const tournamentEnded = factorySafeTimeToDayjs(
    availableDates[availableDates.length - 1]?.value,
    localTimeZone,
  )?.isBefore(todayDate);

  return {
    text: (() => {
      if (tournamentStarted && !tournamentEnded) return todayDate.format('dddd, MMMM Do YYYY');
      else if (tournamentEnded) return endDateMoment?.format('dddd, MMMM Do YYYY');
      return startDateMoment?.format('dddd, MMMM Do YYYY');
    })(),
    value: (() => {
      if (tournamentStarted && !tournamentEnded) return todayDate.format('YYYY-MM-DD');
      else if (tournamentEnded) return endDateMoment?.format('YYYY-MM-DD');
      return startDateMoment?.format('YYYY-MM-DD');
    })(),
  };
};

export const getCourtToAssignMatch = (
  matchUp?: MatchUpInterface,
  selectedCourt?: CourtType,
  selectedTeamCourts?: CourtType[],
  courtsData?: any[],
) => {
  const courtToAssign = courtsData?.find((court) => {
    if (matchUp?.schedule?.venueId) {
      return court?.venueId === matchUp?.schedule?.venueId && !court?.matchUps?.length;
    }
    return !court?.matchUps?.length;
  });

  if (selectedCourt) return selectedCourt;
  if (selectedTeamCourts?.length) return selectedTeamCourts;
  return matchUp?.matchUpType === MatchUpTypeEnum.TEAM ? [courtToAssign] : courtToAssign;
};

export const getCheckedCourts = (
  courtsData: any[],
  selectedDate: DateFilterOption,
  checkedFacilitiesIds: string[],
  disabledVenueIds: string[],
  localTimeZone: string,
) => {
  const { availableDates } = getStartAndAvailableDates(undefined, undefined, localTimeZone);

  return courtsData.filter(
    (court) =>
      checkedFacilitiesIds.includes(court.venueId) &&
      !disabledVenueIds.includes(court.venueId) &&
      !isCourtDisabled(court.extensions, selectedDate?.value) &&
      court.dateAvailability?.some((availability) =>
        availableDates.some(
          (date) =>
            factorySafeTimeToDayjs(availability.date, localTimeZone)?.format('YYYY-MM-DD') ===
            factorySafeTimeToDayjs(date.value, localTimeZone)?.format('YYYY-MM-DD'),
        ),
      ),
  );
};

export const displayScore = (matchUp: MatchUpInterface, className: string, t: TFunction) => {
  const mappedStatus = getStatus(matchUp.matchUpStatus, matchUp.matchUpStatusCodes || []);
  return matchUp.matchUpStatus === MatchUpStatusEnum.AWAITING_RESULT ? (
    <div className={className}>
      <Typography>{t('awaiting')}</Typography>
    </div>
  ) : (
    <Typography>{firstCapital(mappedStatus.pretty)}</Typography>
  );
};
